import { listShipments, type Shipment } from '@lognai/plugin-shipments-ui';

export type ShipmentCalendarView = 'day' | 'week' | 'month';
export type ShipmentCalendarSchedule = 'arrival' | 'departure';

export const calendarDateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
export const calendarDate = (key: string): Date => new Date(`${key}T12:00:00`);
export const addCalendarDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, 12);
const monday = (date: Date): Date => addCalendarDays(date, -((date.getDay() + 6) % 7));

export function shipmentCalendarRange(selectedDate: string, view: ShipmentCalendarView) {
  const anchor = calendarDate(selectedDate);
  const start = view === 'month'
    ? monday(new Date(anchor.getFullYear(), anchor.getMonth(), 1, 12))
    : view === 'week' ? monday(anchor) : anchor;
  const end = view === 'month'
    ? addCalendarDays(monday(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 12)), 6)
    : view === 'week' ? addCalendarDays(start, 6) : anchor;
  const days: string[] = [];
  for (let day = start; day <= end; day = addCalendarDays(day, 1)) days.push(calendarDateKey(day));
  return { start, end, from: calendarDateKey(start), to: calendarDateKey(end), days };
}

export function shipmentScheduleDate(shipment: Shipment, schedule: ShipmentCalendarSchedule): string | undefined {
  const value = schedule === 'arrival' ? shipment.estimatedArrival : shipment.estimatedDeparture;
  if (!value) return undefined;
  // Preserve date-only plans; timestamps are shown on the viewer's local day,
  // matching the local start/end boundaries used by the shipment list API.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = dateOnly ? calendarDate(value) : new Date(value);
  if (!Number.isFinite(date.getTime())) return undefined;
  const key = calendarDateKey(date);
  return dateOnly && key !== value ? undefined : key;
}

/** Follow all pages in the displayed range, never silently truncate to one page. */
export async function loadShipmentCalendar(
  from: string, to: string, schedule: ShipmentCalendarSchedule, signal: AbortSignal
): Promise<Shipment[]> {
  const shipments = new Map<string, Shipment>();
  const start = calendarDate(from);
  start.setHours(0, 0, 0, 0);
  const end = calendarDate(to);
  end.setHours(23, 59, 59, 999);
  const range = { from: start.toISOString(), to: end.toISOString() };
  let page = 1;
  for (;;) {
    signal.throwIfAborted();
    const result = await listShipments({
      page, limit: 100,
      filter: schedule === 'arrival' ? { estimatedArrival: range } : { estimatedDeparture: range },
      sortBy: schedule === 'arrival' ? 'estimatedArrival' : 'estimatedDeparture', sortOrder: 'asc',
    });
    signal.throwIfAborted();
    for (const shipment of result.items) {
      const date = shipmentScheduleDate(shipment, schedule);
      const finished = ['draft', 'cancelled', 'delivered', 'completed'].includes(shipment.status)
        || (schedule === 'arrival' ? shipment.status === 'arrived' || !!shipment.actualArrival
          : ['in_transit', 'arrived'].includes(shipment.status) || !!shipment.actualDeparture);
      if (!finished && date && date >= from && date <= to) shipments.set(shipment.id, shipment);
    }
    if (!result.pagination?.hasNext) break;
    if (result.items.length === 0) throw new Error('The shipment calendar could not load every page. Please retry.');
    page += 1;
  }
  return [...shipments.values()];
}
